import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { Eye, Heart, CheckCircle2, Sparkles } from 'lucide-react';
// Animation variants
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
// Animated Counter Component
function AnimatedCounter({
  end,
  suffix = '',
  duration = 2000
}: {
  end: number;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (isInView) {
      let startTime: number | null = null;
      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        // easeOutQuart
        const easeProgress = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeProgress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);
  return <span ref={ref} data-id="element-101" data-mp="2dg4fasbma991">
      {count}
      {suffix}
    </span>;
}
const VALUES = [{
  icon: Eye,
  title: 'Clarity',
  description: 'We strip away complexity so you can see what truly matters.'
}, {
  icon: Heart,
  title: 'Care',
  description: 'Every service is delivered with genuine warmth and attention.'
}, {
  icon: CheckCircle2,
  title: 'Consistency',
  description: 'We show up. Every time. No excuses.'
}, {
  icon: Sparkles,
  title: 'Courage',
  description: 'We empower you to dream bigger than you thought possible.'
}];
const TEAM = [{
  name: 'Naledi Mokoena',
  role: 'Founder & CEO',
  bio: 'Former corporate strategist turned lifestyle architect. Naledi believes every woman deserves a team in her corner.',
  image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=800&auto=format&fit=crop'
}, {
  name: 'Thandi Nkosi',
  role: 'Head of Wellness',
  bio: 'Certified fitness coach and nutritionist with 10 years of experience transforming busy lives.',
  image: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?q=80&w=800&auto=format&fit=crop'
}, {
  name: 'Amara Osei',
  role: 'Operations Director',
  bio: 'The systems thinker who makes sure every service runs like clockwork.',
  image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=800&auto=format&fit=crop'
}, {
  name: 'Lerato Dlamini',
  role: 'Community Lead',
  bio: 'Connector, curator, and the heart of the Silvermark community.',
  image: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?q=80&w=800&auto=format&fit=crop'
}];
export function About() {
  return <main className="flex flex-col min-h-screen bg-warmwhite pt-24" data-id="element-102" data-mp="ev27wh7p83nu">
      {/* HERO SECTION */}
      <section className="py-16 md:py-24 relative overflow-hidden" data-id="element-103" data-mp="9y3bfs0kbkmh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-104" data-mp="4z8lm646gvsv">
          <motion.div initial="hidden" animate="visible" variants={fadeUpVariants} data-id="element-105" data-mp="12mr2k47zt47y">
            <span className="text-fuchsia font-medium tracking-wider uppercase text-sm mb-4 block" data-id="element-106" data-mp="xnnx4io3ovzg">
              Our Story
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-plum mb-6" data-id="element-107" data-mp="omga4wr1bhko">
              How Silvermark was born
            </h1>
            <p className="text-lg md:text-xl text-charcoal/70 max-w-2xl mx-auto font-body leading-relaxed" data-id="element-108" data-mp="3dftxpvs3w507">
              A journey from overwhelm to clarity, built for the woman who
              refuses to choose between her ambitions and her wellbeing.
            </p>
          </motion.div>
        </div>
        {/* Subtle background accent */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-[800px] h-[800px] bg-plum/5 rounded-full blur-3xl -z-10" data-id="element-109" data-mp="r5t4amooim8j" />
      </section>

      {/* BRAND STORY */}
      <section className="py-16 md:py-24" data-id="element-110" data-mp="w2jljdc7znvj">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-111" data-mp="1ju7334nldml5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-112" data-mp="1otjpxpegqtnq">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="space-y-6 font-body text-charcoal/80 text-lg leading-relaxed" data-id="element-113" data-mp="1orh1h9syl8l4">
              <p data-id="element-114" data-mp="3gqhz1j3o57p">
                It started with a simple observation: the most capable women she
                knew were drowning in details. Not because they couldn't handle
                it — but because handling everything left no room for dreaming.
              </p>
              <p data-id="element-115" data-mp="5uvkqvphi955">
                Naledi Mokoena had spent fifteen years in corporate, managing
                teams, budgets, and impossible deadlines. She was brilliant at
                making things work for everyone else. But her own life? That was
                a different story. Missed gym sessions. Takeout dinners. A
                passport gathering dust.
              </p>
              <p data-id="element-116" data-mp="34aoh5me4h0gy">
                One evening, after cancelling plans for the third time that
                week, she asked herself: What if there was one place — one team
                — that could handle all of it? Not just the cleaning or the
                admin, but the whole picture. Fitness. Nutrition. Travel.
                Business compliance. Property. Events. Everything.
              </p>
              <p className="font-display text-2xl font-semibold text-plum pt-4" data-id="element-117" data-mp="3ba5cy19ikhkj">
                That question became Silvermark Investments CC.
              </p>
              <p data-id="element-118" data-mp="3fa7e89bc78za">
                Today, Silvermark serves over 1,200 women across four countries.
                We're not a luxury — we're a necessity for the woman who refuses
                to choose between her ambitions and her wellbeing.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="relative" data-id="element-119" data-mp="2scvtik46v43a">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden relative z-10" data-id="element-120" data-mp="lk1zsxx0h070">
                <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=1000&auto=format&fit=crop" alt="Confident professional woman" className="w-full h-full object-cover" data-id="element-121" data-mp="263f9uyftwsom" />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-gold rounded-full -z-10" data-id="element-122" data-mp="1rd833fz6oy3r" />
              <div className="absolute -top-6 -right-6 w-32 h-32 border-4 border-fuchsia rounded-full -z-10" data-id="element-123" data-mp="1nd5x4umf1iio" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* MISSION STATEMENT */}
      <section className="py-32 bg-plum relative overflow-hidden" data-id="element-124" data-mp="1deqgjll0lrnf">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-fuchsia/20 via-transparent to-transparent" data-id="element-125" data-mp="18fx4zypagapw" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-126" data-mp="37pvj0mkd7y16">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUpVariants} className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight" data-id="element-127" data-mp="32r220k5vdow9">
            We exist to give the modern woman one thing:{' '}
            <span className="text-gold" data-id="element-128" data-mp="26vrghxf7xmmv">clarity.</span>
          </motion.h2>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 bg-warmwhite" data-id="element-129" data-mp="1szdz0oaopl8e">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-130" data-mp="1xwxetsn1662g">
          <div className="text-center mb-16" data-id="element-131" data-mp="1xu1g423czdcy">
            <h2 className="font-display text-4xl font-bold text-plum mb-4" data-id="element-132" data-mp="1jxo6tg8zai9k">
              Our Values
            </h2>
            <p className="text-charcoal/70 max-w-2xl mx-auto" data-id="element-133" data-mp="1nyq8s4ua58f8">
              The principles that guide every interaction, every service, and
              every decision we make.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-134" data-mp="3ibg32nok8jn5">
            {VALUES.map((value, index) => {
            const Icon = value.icon;
            return <motion.div key={index} variants={fadeUpVariants} className="bg-white p-8 rounded-2xl shadow-sm border-l-4 border-plum flex gap-6 items-start" data-id="element-135" data-mp="1blvz2cwpk3nt" data-mpi={index}>
                  <div className="w-12 h-12 rounded-full bg-plum/10 flex items-center justify-center flex-shrink-0" data-id="element-136" data-mp="1zdb3qcbyix28">
                    <Icon className="w-6 h-6 text-plum" data-id="element-137" data-mp="1ue89zo1fgd9k" />
                  </div>
                  <div data-id="element-138" data-mp="4za6fvw22i49">
                    <h3 className="font-display text-2xl font-bold text-charcoal mb-2" data-id="element-139" data-mp="50lalwtexrvw">
                      {value.title}
                    </h3>
                    <p className="text-charcoal/70 font-body leading-relaxed" data-id="element-140" data-mp="2tjv64i8bta">
                      {value.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* SOCIAL PROOF NUMBERS */}
      <section className="py-20 bg-gold border-y border-gold-dark/20" data-id="element-141" data-mp="2ye81vgefptqu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-142" data-mp="2yg2lmrdyzftc">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-143" data-mp="3v3fneof202ur">
            {[{
            num: 1200,
            suffix: '+',
            label: 'Women Served'
          }, {
            num: 13,
            suffix: '',
            label: 'Services'
          }, {
            num: 4,
            suffix: '',
            label: 'Countries'
          }, {
            num: 98,
            suffix: '%',
            label: 'Client Satisfaction'
          }].map((stat, index) => <div key={index} className="flex flex-col items-center" data-id="element-144" data-mp="l9cuuxboa9qc" data-mpi={index}>
                <div className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-charcoal mb-2" data-id="element-145" data-mp="q7e9n622fmw0">
                  <AnimatedCounter end={stat.num} suffix={stat.suffix} data-id="element-146" data-mp="v44qhvlp2mih" />
                </div>
                <div className="text-charcoal/80 font-medium uppercase tracking-wider text-sm" data-id="element-147" data-mp="z82sce2embf8">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-24 bg-warmwhite" data-id="element-148" data-mp="1dw78pbdziauu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-149" data-mp="1dxie1fn2vqo2">
          <div className="text-center mb-16" data-id="element-150" data-mp="1ivs9lrcpsta7">
            <h2 className="font-display text-4xl font-bold text-plum mb-4" data-id="element-151" data-mp="1iuqunbskvmn7">
              The faces behind your clarity
            </h2>
            <p className="text-charcoal/70 max-w-2xl mx-auto" data-id="element-152" data-mp="1rvylj8srll79">
              Meet the dedicated professionals who make it all happen.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-153" data-mp="3nzd269t3i4lx">
            {TEAM.map((member, index) => <motion.div key={index} variants={fadeUpVariants} className="text-center group" data-id="element-154" data-mp="bgx02bsos4g4" data-mpi={index}>
                <div className="w-48 h-48 mx-auto rounded-full overflow-hidden mb-6 relative" data-id="element-155" data-mp="6ky5g6grlq79">
                  <div className="absolute inset-0 bg-plum/20 group-hover:bg-transparent transition-colors duration-300 z-10" data-id="element-156" data-mp="kga8r28zkozs" />
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" data-id="element-157" data-mp="2m9hmi78yayjk" />
                </div>
                <h3 className="font-display text-xl font-bold text-charcoal mb-1" data-id="element-158" data-mp="wtdyboxw2kj6">
                  {member.name}
                </h3>
                <p className="text-fuchsia font-medium text-sm mb-4" data-id="element-159" data-mp="o0k50nllxkmb">
                  {member.role}
                </p>
                <p className="text-charcoal/70 text-sm leading-relaxed" data-id="element-160" data-mp="s14785l1fefb">
                  {member.bio}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-16 border-t border-charcoal/10 bg-white" data-id="element-161" data-mp="j2ily2o6llau">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-162" data-mp="52rfqo3wp97e">
          <p className="text-center text-sm font-medium text-charcoal/50 uppercase tracking-widest mb-8" data-id="element-163" data-mp="92rwifeh9whm">
            Our Partners & Sponsors
          </p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-50 grayscale hover:grayscale-0 transition-all duration-300" data-id="element-164" data-mp="1zg8srh6oqno6">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="flex items-center justify-center w-32 h-12" data-id="element-165" data-mp="1epwkzh5i09cs">
                <span className="font-display text-xl font-bold text-charcoal" data-id="element-166" data-mp="19phacxclpfyi">
                  Partner {i}
                </span>
              </div>)}
          </div>
        </div>
      </section>
    </main>;
}